'use client'

import { Box, Grid } from '@mui/material'
import { memo } from 'react'
import DewiCardGrid from '@components/DewiCardGrid'
import DewiCard from '@components/DewiCard'
import { Text } from '@components/Helpers'
import { useTranslation } from 'react-i18next'
import CryingCat from '@assets/crying-cat.webp'
import Image from 'next/image'
import { CardItem } from '@data/collection'

type DewiCardListProps = {
  items: CardItem[]
}

/**
 * The collection grid.
 *
 * Everything is rendered in one pass from the static manifest — no pagination,
 * no infinite scroll, no "load more" tile. `content-visibility: auto` keeps
 * that cheap: the browser skips layout and paint for tiles that are off screen,
 * and `contain-intrinsic-size` keeps the scrollbar honest while they are.
 */
function DewiCardList({ items }: DewiCardListProps) {
  const { t } = useTranslation()

  return (
    <DewiCardGrid>
      {items.length === 0 && (
        <Box display="flex" alignItems="center" marginTop="1rem">
          <Text
            sx={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginRight: '0.4rem',
            }}
          >
            {t('generic.noResults')}
          </Text>
          <Image alt="" src={CryingCat} width={30} height={30} />
        </Box>
      )}
      {items.map((item) => (
        <Grid
          key={item.key}
          item
          xs={5}
          sm={5}
          md={2.8}
          lg={2.8}
          xl={1.8}
          sx={{
            contentVisibility: 'auto',
            containIntrinsicSize: 'auto 320px',
          }}
        >
          <DewiCard
            n={item.n}
            name={item.name}
            hasImage={item.hasImage}
            isLegendary={item.isLegendary}
            isBurnt={item.isBurnt}
            href={item.href}
          />
        </Grid>
      ))}
    </DewiCardGrid>
  )
}

export default memo(DewiCardList)
